import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { eligibilitiesApi } from '../eligibilitiesApi';
import type { EligibilityListItem } from '../eligibilityTypes';
import { sampleEligibility } from '../utils/sampleEligibility';

/**
 * Create or edit one eligibility.
 *
 * A name and a note — there is nothing else on the row. No order field, because there is no
 * rank among eligibilities the way there is among tiers: the catalog reads alphabetically.
 */
export function EligibilityModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: EligibilityListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [entailsLicense, setEntailsLicense] = useState(editing?.entailsLicense ?? false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
      entailsLicense,
    };

    try {
      if (editing) await eligibilitiesApi.update(editing.id, request);
      else await eligibilitiesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the eligibility.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, { idPrefix: 'eligibility' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit eligibility' : 'New eligibility'}
      size="lg"
      id="eligibility-modal"
      // Create only. On a form over existing data it would overwrite the record's real values.
      autogenerate={
        editing === null
          ? {
              onGenerate: () => setName(sampleEligibility().name),
              disabled: saving,
            }
          : undefined
      }
      confirm={{
        id: 'eligibility-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="eligibility-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="eligibility-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="eligibility-name"
          label="Name"
          required
          description="What the eligibility is called, e.g. Career Service (Professional). Held once among the live rows."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={150}
          disabled={saving}
        />

        <TextField
          id="eligibility-description"
          label="Description"
          description="Optional note for an administrator reading the catalog."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
        />

        {/*
          What this decides is on the PERSONNEL form, not here — which is why the help text says
          so. CS Form 212 asks for a licence number and its date of validity where the
          eligibility entails one, and an administrator marking it here is what makes those
          fields appear when somebody records having earned it.
        */}
        <label
          htmlFor="eligibility-entails-license"
          className="flex items-start gap-2 text-sm text-slate-600"
        >
          <Checkbox
            id="eligibility-entails-license"
            name="eligibility-entails-license"
            checked={entailsLicense}
            onChange={() => setEntailsLicense((v) => !v)}
            disabled={saving}
          />
          <span>
            Holding this eligibility means holding a licence.
            <span className="block text-xs text-slate-500">
              RA 1080 and the board and bar eligibilities under it. A personnel record that earns
              it is then asked for the licence number and the date it is valid until.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
